import { mapValues } from '@exodus/basic-utils';
import ExchangeProviders, { AssetsService, OrdersService, PricingService, Providers, } from '@exodus/exchange-client-providers';
import createFetchival from '@exodus/fetch/create-fetchival';
import ExodusModule from '@exodus/module';
const MODULE_ID = 'exchangeProviders';
export class ExchangeProvidersModule extends ExodusModule {
    #exchangeProviders;
    #geolocationAtom;
    #exchangeRemoteConfigAtom;
    #subscriptions = [];
    constructor({ config, fetch, geolocationAtom, logger, exchangeTracer, exchangeRemoteConfigAtom, }) {
        super({ MODULE_ID, logger });
        const fetchival = createFetchival({ fetch });
        const ecpLogger = config.debug
            ? mapValues(logger, (loggerFn) => (...args) => loggerFn('exchange-client-providers', ...args))
            : undefined;
        const assetsService = new AssetsService({
            ...config.services.assets,
            fetchival,
        });
        const ordersService = new OrdersService({
            ...config.services.orders,
            fetchival,
        });
        const pricingService = new PricingService({
            ...config.services.pricing,
            fetchival,
        });
        this.#exchangeProviders = new ExchangeProviders([
            config.providers.exodus.enabled &&
                new Providers.Exodus({
                    ...config.providers.exodus,
                    fetchival,
                    assetsService,
                    tracer: exchangeTracer,
                }),
            config.providers.jupiter?.enabled &&
                new Providers.Jupiter({
                    ...config.providers.jupiter,
                    assetsService,
                    ordersService,
                    pricingService,
                    fetchival,
                    tracer: exchangeTracer,
                }),
            config.providers.lifi?.enabled &&
                new Providers.LiFi({
                    ...config.providers.lifi,
                    assetsService,
                    ordersService,
                    pricingService,
                    fetchival,
                    tracer: exchangeTracer,
                }),
            config.providers.rango?.enabled &&
                new Providers.Rango({
                    ...config.providers.rango,
                    assetsService,
                    ordersService,
                    pricingService,
                    fetchival,
                    tracer: exchangeTracer,
                }),
        ].filter(Boolean), { logger: ecpLogger });
        this.#geolocationAtom = geolocationAtom;
        this.#exchangeRemoteConfigAtom = exchangeRemoteConfigAtom;
    }
    getProvider(...args) {
        return this.#exchangeProviders.getProvider(...args);
    }
    getSupportedProvidersForPair(...args) {
        return this.#exchangeProviders.getSupportedProvidersForPair(...args);
    }
    getSupportedFromAssetIds(...args) {
        return this.#exchangeProviders.getSupportedFromAssetIds(...args);
    }
    getSupportedToAssetIds(...args) {
        return this.#exchangeProviders.getSupportedToAssetIds(...args);
    }
    getQuotes(...args) {
        return this.#exchangeProviders.getQuotes(...args);
    }
    prepareExchange(...args) {
        return this.#exchangeProviders.prepareExchange(...args);
    }
    executeExchange(...args) {
        return this.#exchangeProviders.executeExchange(...args);
    }
    getExchangeLimits(...args) {
        return this.#exchangeProviders.getExchangeLimits(...args);
    }
    isAssetExchangeable(...args) {
        return this.#exchangeProviders.isAssetExchangeable(...args);
    }
    load = () => {
        const geolocationSubscription = this.#geolocationAtom.observe(this.#handleGeolocationChange);
        const remoteConfigSubscription = this.#exchangeRemoteConfigAtom.observe(this.#handleRemoteConfigChange);
        this.#subscriptions.push(geolocationSubscription, remoteConfigSubscription);
        this.getSupportedFromAssetIds();
    };
    stop = () => {
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
    };
    #handleGeolocationChange = (geolocationValue) => {
        this.#exchangeProviders.setGeolocation(geolocationValue);
    };
    #handleRemoteConfigChange = async (value) => {
        Object.entries(value).forEach(([providerName, providerConfig]) => {
            if (typeof providerConfig.enabled !== 'boolean') {
                return;
            }
            if (providerConfig.enabled) {
                this.#exchangeProviders.enableProvider(providerName);
            }
            else {
                this.#exchangeProviders.disableProvider(providerName);
            }
        });
    };
}
const exchangeProvidersModuleFactory = (args) => new ExchangeProvidersModule({ ...args });
const exchangeProvidersModuleDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: exchangeProvidersModuleFactory,
    dependencies: [
        'config',
        'logger',
        'geolocationAtom',
        'fetch',
        'exchangeTracer?',
        'exchangeRemoteConfigAtom',
    ],
};
export default exchangeProvidersModuleDefinition;
